import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { DashboardLayout } from "../../components/layout/DashboardLayout";
import { Card, CardContent } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Badge } from "../../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../components/ui/table";
import {
  documentsAPI,
  entitiesAPI,
  breachReportsAPI,
  dpiaAPI,
  dsrV2API,
  ropaAPI,
  vendorV2API,
} from "../../services/api";
import { useAuth } from "../../contexts/AuthContext";
import { useGlobalContext } from "../../contexts/GlobalContext";
import { DocumentStatusBadge } from "../../components/documents/DocumentStatusBadge";
import { DocumentTypeBadge } from "../../components/documents/DocumentTypeBadge";
import {
  FolderOpen,
  Plus,
  Search,
  Edit,
  MoreHorizontal,
  FileText,
  Download,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../../components/ui/dropdown-menu";
import { toast } from "sonner";
import { format } from "date-fns";

const initialFormData = {
  organization_id: "",
  entity_id: "",      // null/empty = client-wide; entity admins are auto-tagged on submit
  title: "",
  description: "",
  document_type: "",
  file_url: "",
  version: "1.0",
  status: "draft",
  module: "",
  source_module: "",
  source_id: "",
  review_date: "",
};

const SOURCE_MODULE_ROUTES = {
  breach: (id) => `/compliance/breaches/workflow/${id}`,
  dpia: (id) => `/risk/dpia/${id}`,
  dsr: (id) => `/compliance/dsr/workflow/${id}`,
  ropa: (id) => `/compliance/ropa/view/${id}`,
  assessment: () => `/compliance/maturity`,
  contract: () => `/compliance/vendors`,
};

export default function DocumentsPage() {
  const navigate = useNavigate();
  const { isDPO, user, isReadOnly, isComplianceReadOnly, isClientAdmin, isEntityAdmin, isClientViewer, isEntityViewer } = useAuth();
  const { selectedClientId, selectedEntityId, setSelectedClientId, setSelectedEntityId, clients, entities } =
    useGlobalContext();
  const isEntityScoped = isEntityAdmin || isEntityViewer;
  const isClientScoped = isClientAdmin || isClientViewer;
  // `isComplianceReadOnly` covers both viewers and super_admin — used throughout
  // the compliance UI to gate write affordances.
  const cannotWrite = isComplianceReadOnly;
  const formRef = React.useRef(null);
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterType, setFilterType] = useState(""); // document type filter
  const [filterStatus, setFilterStatus] = useState(""); // "approved", "under_review", "draft", "superseded", or ""
  const [filterReviewDue, setFilterReviewDue] = useState(""); // "overdue", "this_month", "this_quarter", or ""
  const [showForm, setShowForm] = useState(false); // Inline form visibility
  const [editing, setEditing] = useState(null);
  const [formData, setFormData] = useState(initialFormData);
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null); // "creating", "uploading", null
  const [currentPage, setCurrentPage] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [itemsPerPage, setItemsPerPage] = useState(50);
  const [moduleRecords, setModuleRecords] = useState([]); // Records for selected module
  const [loadingRecords, setLoadingRecords] = useState(false);
  const [formEntities, setFormEntities] = useState([]);  // Entities for the form's selected client
  const [filterEntityId, setFilterEntityId] = useState(""); // Optional entity filter pill ("" all, "client_wide" only nulls, or entity id)
  const [filterEntities, setFilterEntities] = useState([]); // Entities under the currently selected client filter

  // Scroll to form when opened
  useEffect(() => {
    if (showForm && formRef.current) {
      setTimeout(() => {
        formRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 100);
    }
  }, [showForm]);

  const fetchDocuments = useCallback(async () => {
    try {
      setLoading(true);
      const skip = currentPage * itemsPerPage;
      const docsRes = await documentsAPI.getAll(
        selectedEntityId || undefined,
        selectedClientId || undefined,
        itemsPerPage,
        skip,
        filterEntityId || undefined,
      );
      setDocuments(docsRes.data.data);
      setTotalCount(docsRes.data.total);
      setHasMore(docsRes.data.hasMore);
    } catch (error) {
      toast.error("Failed to fetch documents");
    } finally {
      setLoading(false);
    }
  }, [selectedEntityId, selectedClientId, filterEntityId, currentPage, itemsPerPage]);

  useEffect(() => {
    fetchDocuments();
  }, [fetchDocuments]);

  // Fetch entities for the form's selected client (powers the form's Entity dropdown)
  useEffect(() => {
    const cid = formData.organization_id;
    if (!cid) {
      setFormEntities([]);
      return;
    }
    entitiesAPI
      .getAll(cid)
      .then((res) => setFormEntities(res.data || []))
      .catch(() => setFormEntities([]));
  }, [formData.organization_id]);

  // Fetch entities for the filter bar's selected client (powers the Entity filter pill)
  useEffect(() => {
    const cid = selectedClientId || (isClientScoped || isEntityScoped ? user?.client_id : "");
    if (!cid) {
      setFilterEntities([]);
      return;
    }
    entitiesAPI
      .getAll(cid)
      .then((res) => setFilterEntities(res.data || []))
      .catch(() => setFilterEntities([]));
  }, [selectedClientId, user?.client_id, isClientScoped, isEntityScoped]);

  // Reset entity filter when org filter changes (would otherwise reference a stale entity)
  useEffect(() => {
    setFilterEntityId("");
  }, [selectedClientId]);

  // Resolve entity id → display name
  const getDocumentEntityLabel = (doc) => {
    if (!doc.entity_id) return null;
    if (doc.entity_name) return doc.entity_name;
    const found = formEntities.find((e) => e.id === doc.entity_id) || filterEntities.find((e) => e.id === doc.entity_id);
    return found?.entity_name || `#${doc.entity_id.slice(0, 6)}`;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Spec Section 3: "at least one of file_path or file_url is required"
    // Enforce on creation. On edit, existing attached file counts as satisfying the rule.
    const hasUrl = (formData.file_url || "").trim().length > 0;
    const hasExistingFile = !!(editing && editing.s3_key);
    if (!selectedFile && !hasUrl && !hasExistingFile) {
      toast.error("Attach a file or paste a URL — documents must have a source.");
      return;
    }

    try {
      // Entity Admins are auto-tagged to their entity client-side (backend re-enforces).
      // Client/Entity admins cannot create Global (client_id=null); the backend will reject.
      const resolvedClient = isEntityScoped || isClientScoped
        ? user?.client_id
        : (formData.organization_id || null);
      const resolvedEntity = isEntityScoped
        ? (user?.entity_id || null)
        : (formData.entity_id || null);

      const submitData = {
        ...formData,
        // Maps form field organization_id to backend client_id (company-wide documents)
        client_id: resolvedClient === "" ? null : resolvedClient,
        entity_id: resolvedEntity || null,
        source_module: formData.source_module === "" ? null : formData.source_module,
        source_id: formData.source_module === "" ? null : formData.source_id,
      };
      delete submitData.organization_id;

      setSubmitStatus(editing ? "updating" : "creating");
      let documentId;
      if (editing) {
        await documentsAPI.update(editing.id, submitData);
        documentId = editing.id;
      } else {
        const response = await documentsAPI.create(submitData);
        documentId = response.data.id || response.data._id;
      }

      // Auto-upload file if selected
      if (selectedFile) {
        setSubmitStatus("uploading");
        const formDataFile = new FormData();
        formDataFile.append("file", selectedFile);

        const uploadResponse = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/documents/${documentId}/upload`, {
          method: "POST",
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
          body: formDataFile,
        });

        if (!uploadResponse.ok) {
          const error = await uploadResponse.json();
          throw new Error(error.detail || "Upload failed");
        }
      }

      toast.success(editing ? "Document updated" : "Document created");
      if (selectedFile) {
        toast.success("File uploaded successfully");
      }

      setShowForm(false);
      resetForm();
      fetchDocuments();
    } catch (error) {
      const errorMsg =
        error.response?.data?.detail?.[0]?.msg || error.response?.data?.detail || error.message || "Operation failed";
      toast.error(errorMsg);
    } finally {
      setSubmitStatus(null);
      setUploading(false);
    }
  };

  const handleEdit = (doc) => {
    setEditing(doc);
    setFormData({
      organization_id: doc.client_id || "", // Maps backend client_id to form field organization_id
      entity_id: doc.entity_id || "",
      title: doc.title,
      description: doc.description || "",
      document_type: doc.document_type,
      file_url: doc.file_url || "",
      version: doc.version,
      status: doc.status,
      module: doc.module || "",
      source_module: doc.source_module || "",
      source_id: doc.source_id || "",
      review_date: doc.review_date ? doc.review_date.split("T")[0] : "",
    });
    setShowForm(true);
  };

  const resetForm = () => {
    setEditing(null);
    setFormData({
      ...initialFormData,
      // Pre-fill scope from user profile so non-DPO users don't accidentally submit Global
      organization_id: isDPO ? "" : (user?.client_id || ""),
      entity_id: isEntityScoped ? (user?.entity_id || "") : "",
    });
    setSelectedFile(null);
    setSubmitStatus(null);
    setShowForm(false);
  };

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      setSelectedFile(file);
    }
  };

  const handleDownloadDocument = async (documentId) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/documents/${documentId}/download`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });

      if (!response.ok) throw new Error(`Download failed: ${response.statusText}`);

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error("Failed to download document");
    }
  };

  const getOrgName = (orgId) => clients.find((o) => o.id === orgId)?.client_name || "Global";

  // Fetch records from the selected module's API
  const fetchModuleRecords = useCallback(async (moduleType) => {
    if (!moduleType || moduleType === "__none__") {
      setModuleRecords([]);
      return;
    }

    // Per-module adapter: { fetch: () => axios promise, label: (record) => display string }
    const adapters = {
      breach: {
        fetch: () => breachReportsAPI.getAll(),
        label: (r) =>
          r.breach_reference
            ? `${r.breach_reference} — ${r.title || "Untitled"}`
            : r.title || `Breach #${(r.id || "").slice(0, 8)}`,
      },
      dpia: {
        fetch: () => dpiaAPI.getAll(),
        label: (r) =>
          r.dpia_reference
            ? `${r.dpia_reference} — ${r.project_title || "Untitled"}`
            : r.project_title || `DPIA #${(r.id || "").slice(0, 8)}`,
      },
      dsr: {
        fetch: () => dsrV2API.getAll(),
        label: (r) => {
          const requester = r.requester?.name || "Unknown requester";
          const type = r.request_type || "request";
          return `${type} — ${requester}`;
        },
      },
      ropa: {
        fetch: () => ropaAPI.getAll(),
        label: (r) => r.process_name || `ROPA #${(r.id || "").slice(0, 8)}`,
      },
      contract: {
        fetch: () => vendorV2API.getAll(),
        label: (r) => r.vendor_name || r.name || `Vendor #${(r.id || "").slice(0, 8)}`,
      },
      // assessment (maturity snapshots) has no list endpoint that mirrors the other modules
    };

    const adapter = adapters[moduleType];
    if (!adapter) {
      setModuleRecords([]);
      return;
    }

    try {
      setLoadingRecords(true);
      const res = await adapter.fetch();
      const rows = res?.data || res || [];
      setModuleRecords(rows.map((r) => ({ id: r.id, title: adapter.label(r) })));
    } catch (error) {
      console.error("Failed to fetch module records:", error);
      setModuleRecords([]);
      toast.error("Failed to load records from module");
    } finally {
      setLoadingRecords(false);
    }
  }, []);

  // Fetch records when module selection changes
  useEffect(() => {
    if (formData.source_module) {
      fetchModuleRecords(formData.source_module);
    } else {
      setModuleRecords([]);
    }
  }, [formData.source_module, fetchModuleRecords]);

  // Check for overdue documents
  const overdueDocuments = documents.filter((d) => {
    if (!d.review_date) return false;
    return new Date(d.review_date) < new Date() && d.status?.toLowerCase() !== "superseded";
  });

  const filteredDocs = documents.filter((d) => {
    // Search filter
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      const matchesSearch =
        (d.title && d.title.toLowerCase().includes(search)) ||
        (d.document_type && d.document_type.toLowerCase().includes(search)) ||
        (d.description && d.description.toLowerCase().includes(search)) ||
        getOrgName(d.client_id).toLowerCase().includes(search);
      if (!matchesSearch) return false;
    }

    // Type filter
    if (filterType && d.document_type?.toLowerCase() !== filterType.toLowerCase()) {
      return false;
    }

    // Status filter
    if (filterStatus && d.status?.toLowerCase() !== filterStatus.toLowerCase()) {
      return false;
    }

    // Review due filter
    if (filterReviewDue && d.review_date) {
      const now = new Date();
      const reviewDate = new Date(d.review_date);

      if (filterReviewDue === "overdue") {
        if (reviewDate >= now) return false;
      } else if (filterReviewDue === "this_month") {
        const thisMonthStart = new Date(now.getFullYear(), now.getMonth(), 1);
        const thisMonthEnd = new Date(now.getFullYear(), now.getMonth() + 1, 0);
        if (reviewDate < thisMonthStart || reviewDate > thisMonthEnd) return false;
      } else if (filterReviewDue === "this_quarter") {
        const quarter = Math.floor(now.getMonth() / 3);
        const quarterStart = new Date(now.getFullYear(), quarter * 3, 1);
        const quarterEnd = new Date(now.getFullYear(), quarter * 3 + 3, 0);
        if (reviewDate < quarterStart || reviewDate > quarterEnd) return false;
      }
    }

    return true;
  });

  return (
    <DashboardLayout>
      <div className="p-6 space-y-6" data-testid="documents-page">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold">Documents</h1>
            <p className="text-muted-foreground">Policies, procedures, notices, agreements and compliance records</p>
          </div>
          {!cannotWrite && (
            <Button
              onClick={() => {
                resetForm();
                setShowForm(true);
              }}
              data-testid="add-document-btn"
            >
              <Plus className="h-4 w-4 mr-2" />
              {showForm ? "Hide Form" : "Add Document"}
            </Button>
          )}
        </div>

        {/* Overdue Documents Alert Banner */}
        {overdueDocuments.length > 0 && (
          <div
            style={{
              backgroundColor: "#fee2e2",
              borderLeft: "4px solid #dc2626",
              padding: "16px",
              borderRadius: "6px",
              display: "flex",
              flexDirection: "column",
              gap: "8px",
            }}
          >
            <div style={{ color: "#991b1b", fontSize: "14px", fontWeight: "600" }}>
              {overdueDocuments.length} document{overdueDocuments.length !== 1 ? "s" : ""} overdue for review
            </div>
            <div style={{ color: "#991b1b", fontSize: "13px", lineHeight: "1.5" }}>
              {overdueDocuments.map((doc, idx) => (
                <div key={doc.id}>
                  {getOrgName(doc.client_id)} — {doc.title} (due {doc.review_date ? format(new Date(doc.review_date), "MMM d") : "unknown"})
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Filter Bar - Inline with search */}
        <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center flex-wrap">
          {/* Search Input */}
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search documents..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-9"
            />
          </div>

          {/* Filters */}
          {isDPO && (
            <Select
              value={selectedClientId || "all"}
              onValueChange={(val) => {
                setSelectedClientId(val === "all" ? null : val);
                setCurrentPage(0);
              }}
            >
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="All Organizations" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Organizations</SelectItem>
                {clients.map((org) => (
                  <SelectItem key={org.id} value={org.id}>
                    {org.client_name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}

          {/* Entity filter — hidden for entity-level roles (already locked to their entity) */}
          {!isEntityScoped && filterEntities.length > 0 && (
            <Select
              value={filterEntityId || "all"}
              onValueChange={(val) => {
                setFilterEntityId(val === "all" ? "" : val);
                setCurrentPage(0);
              }}
            >
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="All Entities" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Entities</SelectItem>
                <SelectItem value="client_wide">Client-wide only</SelectItem>
                {filterEntities.map((e) => (
                  <SelectItem key={e.id} value={e.id}>
                    {e.entity_name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}

          {/* Type Filter */}
          <Select
            value={filterType || "__none__"}
            onValueChange={(val) => {
              setFilterType(val === "__none__" ? "" : val);
              setCurrentPage(0);
            }}
          >
            <SelectTrigger className="w-[140px]">
              <SelectValue placeholder="All Types" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__none__">All Types</SelectItem>
              <SelectItem value="policy">Policy</SelectItem>
              <SelectItem value="procedure">Procedure</SelectItem>
              <SelectItem value="notice">Notice</SelectItem>
              <SelectItem value="agreement">Agreement</SelectItem>
              <SelectItem value="training_record">Training Record</SelectItem>
              <SelectItem value="regulatory_correspondence">Regulatory Correspondence</SelectItem>
              <SelectItem value="template">Template</SelectItem>
            </SelectContent>
          </Select>

          {/* Status Filter */}
          <Select
            value={filterStatus || "__none__"}
            onValueChange={(val) => {
              setFilterStatus(val === "__none__" ? "" : val);
              setCurrentPage(0);
            }}
          >
            <SelectTrigger className="w-[140px]">
              <SelectValue placeholder="All Statuses" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__none__">All Statuses</SelectItem>
              <SelectItem value="approved">Approved</SelectItem>
              <SelectItem value="under_review">Under Review</SelectItem>
              <SelectItem value="draft">Draft</SelectItem>
              <SelectItem value="superseded">Superseded</SelectItem>
            </SelectContent>
          </Select>

          {/* Review Due Filter */}
          <Select
            value={filterReviewDue || "__none__"}
            onValueChange={(val) => {
              setFilterReviewDue(val === "__none__" ? "" : val);
              setCurrentPage(0);
            }}
          >
            <SelectTrigger className="w-[140px]">
              <SelectValue placeholder="Review due" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__none__">Review due</SelectItem>
              <SelectItem value="overdue">Overdue</SelectItem>
              <SelectItem value="this_month">This Month</SelectItem>
              <SelectItem value="this_quarter">This Quarter</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <Card>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Document</TableHead>
                  <TableHead className="hidden sm:table-cell">Type</TableHead>
                  <TableHead className="hidden lg:table-cell">Entity</TableHead>
                  <TableHead className="hidden md:table-cell">Linked to</TableHead>
                  <TableHead className="hidden lg:table-cell">Version</TableHead>
                  <TableHead className="hidden md:table-cell">Review due</TableHead>
                  <TableHead className="hidden sm:table-cell">Status</TableHead>
                  <TableHead className="w-12"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={8} className="text-center py-8">
                      Loading...
                    </TableCell>
                  </TableRow>
                ) : filteredDocs.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                      No documents found
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredDocs.map((doc) => (
                    <TableRow key={doc.id}>
                      <TableCell>
                        <div className="flex items-start gap-2">
                          <FileText className="h-4 w-4 text-muted-foreground flex-shrink-0 mt-0.5 hidden sm:inline" />
                          <div className="flex flex-col gap-1.5 min-w-0 w-full">
                            <p className="font-bold leading-tight text-sm sm:text-base truncate">{doc.title}</p>
                            <p className="text-xs text-muted-foreground">{getOrgName(doc.client_id)}</p>
                            <div className="flex flex-wrap items-center gap-1.5 sm:gap-2 mt-1">
                              {doc.module && (
                                <span className="text-xs text-muted-foreground bg-muted px-2 py-0.5 rounded whitespace-nowrap">
                                  {doc.module}
                                </span>
                              )}
                              {doc.s3_key && (
                                <Badge variant="secondary" className="text-xs font-normal whitespace-nowrap">
                                  <Download className="h-3 w-3 mr-1" />
                                  <span className="hidden sm:inline">{doc.file_name || "File attached"}</span>
                                  <span className="sm:hidden">File</span>
                                </Badge>
                              )}
                            </div>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell className="hidden sm:table-cell">
                        <DocumentTypeBadge type={doc.document_type} />
                      </TableCell>
                      {/* Entity column.
                          - entity_id set                  → entity name
                          - client_id set, entity_id null  → "Client-wide" (spans every entity under that client)
                          - client_id null, entity_id null → "DPO level" (Global / pure DPO-internal doc) */}
                      <TableCell className="hidden lg:table-cell text-sm">
                        {doc.entity_id ? (
                          <span>{getDocumentEntityLabel(doc)}</span>
                        ) : doc.client_id ? (
                          <span className="text-muted-foreground italic">Client-wide</span>
                        ) : (
                          <span className="text-muted-foreground italic">DPO level</span>
                        )}
                      </TableCell>
                      {/* Linked to column - shows module and record */}
                      <TableCell className="hidden md:table-cell text-sm" onClick={(e) => e.stopPropagation()}>
                        {doc.source_module && doc.source_id ? (
                          <Badge
                            variant="outline"
                            className="cursor-pointer hover:bg-muted"
                            onClick={() => {
                              const builder = SOURCE_MODULE_ROUTES[doc.source_module];
                              if (builder) navigate(builder(doc.source_id));
                              else toast.error(`No route for ${doc.source_module}`);
                            }}
                          >
                            {doc.source_module} #{doc.source_id?.substring(0, 4)}
                          </Badge>
                        ) : (
                          <span className="text-muted-foreground">—</span>
                        )}
                      </TableCell>
                      <TableCell className="hidden lg:table-cell">v{doc.version}</TableCell>
                      {/* Review due column - red if overdue */}
                      <TableCell className="hidden md:table-cell text-sm">
                        {doc.review_date ? (
                          <span
                            className={
                              new Date(doc.review_date) < new Date()
                                ? "font-bold text-red-600"
                                : "text-muted-foreground"
                            }
                          >
                            {format(new Date(doc.review_date), "MMM d, yyyy")}
                          </span>
                        ) : (
                          <span className="text-muted-foreground">—</span>
                        )}
                      </TableCell>
                      <TableCell className="hidden sm:table-cell">
                        <DocumentStatusBadge status={doc.status} />
                      </TableCell>
                      <TableCell>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon">
                              <MoreHorizontal className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            {doc.s3_key && (
                              <DropdownMenuItem onClick={() => handleDownloadDocument(doc.id)}>
                                <Download className="h-4 w-4 mr-2" />
                                Download File
                              </DropdownMenuItem>
                            )}
                            {doc.file_url && (
                              <DropdownMenuItem onClick={() => window.open(doc.file_url, "_blank")}>
                                <Download className="h-4 w-4 mr-2" />
                                Open Link
                              </DropdownMenuItem>
                            )}
                            {!cannotWrite && (
                              <DropdownMenuItem onClick={() => handleEdit(doc)}>
                                <Edit className="h-4 w-4 mr-2" />
                                Edit
                              </DropdownMenuItem>
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
            {/* Pagination Controls */}
            {totalCount > 0 && (
              <div className="flex flex-col gap-4 p-4 bg-muted rounded-lg">
                {/* Summary and Items Per Page */}
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                  <div className="text-sm text-muted-foreground">
                    Showing {currentPage * itemsPerPage + 1} to {Math.min((currentPage + 1) * itemsPerPage, totalCount)}{" "}
                    of {totalCount} documents
                  </div>
                  <div className="flex items-center gap-3">
                    <label htmlFor="items-per-page" className="text-sm text-muted-foreground">
                      Items per page:
                    </label>
                    <Select
                      value={String(itemsPerPage)}
                      onValueChange={(val) => {
                        setItemsPerPage(Number(val));
                        setCurrentPage(0);
                      }}
                    >
                      <SelectTrigger className="w-[80px] h-9">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="25">25</SelectItem>
                        <SelectItem value="50">50</SelectItem>
                        <SelectItem value="100">100</SelectItem>
                        <SelectItem value="200">200</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* Page Navigation */}
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                  <div className="text-sm font-medium">
                    Page {currentPage + 1} of {Math.ceil(totalCount / itemsPerPage)}
                  </div>
                  <div className="flex items-center gap-2 flex-wrap">
                    {/* Previous Button */}
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCurrentPage((p) => Math.max(0, p - 1))}
                      disabled={currentPage === 0 || loading}
                    >
                      <ChevronLeft className="h-4 w-4 mr-1" />
                      Previous
                    </Button>

                    {/* Page Number Buttons */}
                    <div className="flex gap-1">
                      {Array.from({ length: Math.ceil(totalCount / itemsPerPage) }).map((_, idx) => {
                        const pageNum = idx;
                        const isCurrentPage = pageNum === currentPage;
                        const isVisible =
                          Math.abs(pageNum - currentPage) <= 2 ||
                          pageNum === 0 ||
                          pageNum === Math.ceil(totalCount / itemsPerPage) - 1;

                        if (!isVisible) {
                          if (pageNum === 1 && currentPage > 3)
                            return (
                              <span key="ellipsis-start" className="px-1">
                                ...
                              </span>
                            );
                          return null;
                        }

                        return (
                          <Button
                            key={pageNum}
                            variant={isCurrentPage ? "default" : "outline"}
                            size="sm"
                            onClick={() => setCurrentPage(pageNum)}
                            disabled={loading}
                            className={isCurrentPage ? "min-w-9" : ""}
                          >
                            {pageNum + 1}
                          </Button>
                        );
                      })}
                    </div>

                    {/* Next Button */}
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCurrentPage((p) => p + 1)}
                      disabled={!hasMore || loading}
                    >
                      Next
                      <ChevronRight className="h-4 w-4 ml-1" />
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Inline Form - Below the table */}
        {showForm && (
          <Card ref={formRef} className="border-t-2 border-primary/20">
            <CardContent className="p-6">
              <div className="mb-4">
                <h2 className="text-xl font-bold">{editing ? "Edit Document" : "Add Document"}</h2>
                <p className="text-sm text-muted-foreground">Manage compliance documentation</p>
              </div>
              <form onSubmit={handleSubmit} className="flex flex-col gap-6">
                <div className="grid gap-4">
                  {/* Organisation + Entity scope — visibility depends on role */}
                  {(isDPO || isClientScoped) && (
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      {isDPO && (
                        <div className="space-y-2">
                          <Label>Organization</Label>
                          <Select
                            value={formData.organization_id || "global"}
                            onValueChange={(value) =>
                              !cannotWrite &&
                              setFormData((prev) => ({
                                ...prev,
                                organization_id: value === "global" ? "" : value,
                                entity_id: "", // reset entity when client changes
                              }))
                            }
                            disabled={cannotWrite}
                          >
                            <SelectTrigger>
                              <SelectValue placeholder="Select organization" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="global">Global (All)</SelectItem>
                              {clients.map((org) => (
                                <SelectItem key={org.id} value={org.id}>
                                  {org.client_name}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      )}

                      <div className="space-y-2">
                        <Label>Entity</Label>
                        <Select
                          value={formData.entity_id || "client_wide"}
                          onValueChange={(value) =>
                            !cannotWrite &&
                            setFormData((prev) => ({ ...prev, entity_id: value === "client_wide" ? "" : value }))
                          }
                          disabled={cannotWrite || !formData.organization_id}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder={!formData.organization_id ? "Pick organization first" : "Client-wide (all entities)"} />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="client_wide">Client-wide (all entities)</SelectItem>
                            {formEntities.map((e) => (
                              <SelectItem key={e.id} value={e.id}>
                                {e.entity_name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <p className="text-xs text-muted-foreground">
                          Leave as "Client-wide" to share with every entity under this client.
                        </p>
                      </div>
                    </div>
                  )}
                  {isEntityScoped && (
                    <div className="text-xs text-muted-foreground bg-muted px-3 py-2 rounded">
                      This document will be tagged to your entity automatically.
                    </div>
                  )}
                  <div className="space-y-2">
                    <Label>Title *</Label>
                    <Input
                      value={formData.title}
                      onChange={(e) => !cannotWrite && setFormData((prev) => ({ ...prev, title: e.target.value }))}
                      disabled={cannotWrite}
                      required
                      data-testid="document-title-input"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Document Type *</Label>
                      <Select
                        value={formData.document_type}
                        onValueChange={(value) =>
                          !cannotWrite && setFormData((prev) => ({ ...prev, document_type: value }))
                        }
                        disabled={cannotWrite}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select type" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="policy">Policy</SelectItem>
                          <SelectItem value="procedure">Procedure</SelectItem>
                          <SelectItem value="notice">Notice</SelectItem>
                          <SelectItem value="agreement">Agreement</SelectItem>
                          <SelectItem value="training_record">Training Record</SelectItem>
                          <SelectItem value="regulatory_correspondence">Regulatory Correspondence</SelectItem>
                          <SelectItem value="template">Template</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Version</Label>
                      <Input
                        value={formData.version}
                        onChange={(e) => !cannotWrite && setFormData((prev) => ({ ...prev, version: e.target.value }))}
                        disabled={cannotWrite}
                        placeholder="1.0"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Status</Label>
                      <Select
                        value={formData.status}
                        onValueChange={(value) => !cannotWrite && setFormData((prev) => ({ ...prev, status: value }))}
                        disabled={cannotWrite}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="draft">Draft</SelectItem>
                          <SelectItem value="under_review">Under Review</SelectItem>
                          <SelectItem value="approved">Approved</SelectItem>
                          <SelectItem value="superseded">Superseded</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Review due</Label>
                      <Input
                        type="date"
                        value={formData.review_date}
                        onChange={(e) =>
                          !cannotWrite && setFormData((prev) => ({ ...prev, review_date: e.target.value }))
                        }
                        disabled={cannotWrite}
                      />
                    </div>
                  </div>

                  {/* Module Linking - Compliance Record Section */}
                  <div className="bg-blue-50 dark:bg-blue-950/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
                    <h3 className="font-semibold text-sm text-blue-900 dark:text-blue-400 mb-4 uppercase tracking-wide">
                      Link to Compliance Record (Optional)
                    </h3>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label className="text-gray-700 dark:text-gray-300">Module</Label>
                        <Select
                          value={formData.source_module || "__none__"}
                          onValueChange={(value) =>
                            !cannotWrite &&
                            setFormData((prev) => ({
                              ...prev,
                              source_module: value === "__none__" ? "" : value,
                              source_id: "",
                            }))
                          }
                          disabled={cannotWrite}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="None" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="__none__">None</SelectItem>
                            <SelectItem value="dpia">DPIA</SelectItem>
                            <SelectItem value="breach">Breach</SelectItem>
                            <SelectItem value="dsr">DSR</SelectItem>
                            <SelectItem value="ropa">ROPA</SelectItem>
                            <SelectItem value="assessment">Assessment</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label className="text-gray-700 dark:text-gray-300">Record</Label>
                        <Select
                          value={formData.source_id || "__select__"}
                          onValueChange={(value) =>
                            !cannotWrite &&
                            setFormData((prev) => ({ ...prev, source_id: value === "__select__" ? "" : value }))
                          }
                          disabled={cannotWrite || !formData.source_module || loadingRecords}
                        >
                          <SelectTrigger>
                            <SelectValue
                              placeholder={
                                loadingRecords
                                  ? "Loading records..."
                                  : formData.source_module
                                  ? "Select record"
                                  : "Select module first"
                              }
                            />
                          </SelectTrigger>
                          <SelectContent>
                            {formData.source_module ? (
                              <>
                                <SelectItem value="__select__">Select record</SelectItem>
                                {moduleRecords.length > 0 ? (
                                  moduleRecords.map((record) => (
                                    <SelectItem key={record.id} value={record.id}>
                                      {record.title}
                                    </SelectItem>
                                  ))
                                ) : (
                                  <SelectItem value="__none__" disabled>
                                    No records available
                                  </SelectItem>
                                )}
                              </>
                            ) : (
                              <SelectItem value="__select__" disabled>
                                Select module first
                              </SelectItem>
                            )}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  </div>

                  {/* File Upload - Mockup Style */}
                  <div className="space-y-3">
                    {/* Show existing file if editing and has uploaded file */}
                    {editing && editing.file_name && (
                      <div className="bg-green-50 dark:bg-green-950/20 border border-green-200 dark:border-green-800 rounded-lg p-4 flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <FileText className="h-5 w-5 text-green-600 dark:text-green-400" />
                          <div className="flex flex-col">
                            <p className="text-sm font-medium text-green-900 dark:text-green-400">{editing.file_name}</p>
                            <p className="text-xs text-green-700 dark:text-green-500">Current file attached</p>
                          </div>
                        </div>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            // Optional: allow clearing the file for re-upload
                          }}
                          className="text-green-600 dark:text-green-400 hover:text-green-700"
                        >
                          ✓
                        </Button>
                      </div>
                    )}
                    <div className="border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg p-8 text-center bg-gray-50 dark:bg-gray-900/30 hover:bg-gray-100 dark:hover:bg-gray-900/40 transition">
                      <input
                        id="file-input"
                        type="file"
                        onChange={handleFileSelect}
                        accept=".pdf,.docx,.xlsx,.jpg,.jpeg,.png,.txt,.csv"
                        disabled={uploading || cannotWrite}
                        className="hidden"
                      />
                      <label
                        htmlFor="file-input"
                        className="cursor-pointer flex flex-col items-center gap-2 block"
                      >
                        <FileText className={`h-8 w-8 ${selectedFile ? "text-blue-500" : "text-gray-400"}`} />
                        <div className="text-center">
                          <p className={`font-medium ${selectedFile ? "text-blue-600 dark:text-blue-400" : "text-gray-600 dark:text-gray-400"}`}>
                            {selectedFile ? `✓ ${selectedFile.name}` : editing && editing.file_name ? "Replace file or drag and drop" : "Choose file or drag and drop"}
                          </p>
                          <p className="text-xs text-gray-500 dark:text-gray-500 mt-1">
                            PDF, DOCX, XLSX, JPG, PNG, TXT, CSV · Max 10MB
                          </p>
                        </div>
                      </label>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label>Or paste a URL</Label>
                    <Input
                      value={formData.file_url}
                      onChange={(e) => !cannotWrite && setFormData((prev) => ({ ...prev, file_url: e.target.value }))}
                      disabled={cannotWrite}
                      placeholder="https://..."
                    />
                    <p className="text-xs text-muted-foreground">Link to SharePoint, Google Drive, or any external document</p>
                  </div>

                  <div className="space-y-2">
                    <Label>Description</Label>
                    <Textarea
                      value={formData.description}
                      onChange={(e) => !cannotWrite && setFormData((prev) => ({ ...prev, description: e.target.value }))}
                      disabled={cannotWrite}
                      placeholder="Any context or notes about the document"
                      rows={3}
                    />
                  </div>
                </div>
                <div className="flex gap-3 pt-4 border-t">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => {
                      resetForm();
                      setShowForm(false);
                    }}
                    disabled={!!submitStatus}
                  >
                    Cancel
                  </Button>
                  {!cannotWrite && (
                    <Button type="submit" data-testid="save-document-btn" disabled={!!submitStatus}>
                      {submitStatus === "creating" && "⏳ Creating document..."}
                      {submitStatus === "updating" && "⏳ Updating document..."}
                      {submitStatus === "uploading" && "📤 Uploading file..."}
                      {!submitStatus && (editing ? "Update" : "Add Document")}
                    </Button>
                  )}
                </div>
              </form>
            </CardContent>
          </Card>
        )}
      </div>
    </DashboardLayout>
  );
}
